/**
 * Shared data-grid UX helpers for desktop list tables.
 *
 * Pattern (enterprise table best practice):
 * - Pin the rightmost Actions column so kebab menus stay reachable without
 *   horizontal scrolling (especially on laptop widths).
 * - Make whole rows clickable into their detail destination with a light
 *   grey hover, while stopping propagation on nested controls.
 */

/** Left-edge shadow so pinned actions read as a layer over scrolling cells. */
export const GRID_STICKY_ACTIONS = "grid-sticky-actions";

/** Compact icon-column width (~36px). Kept in sync with `.grid-sticky-actions` CSS. */
export const STICKY_ACTIONS_WIDTH = "2.25rem";
export const STICKY_ACTIONS_WIDTH_WIDE = "3.5rem";
/** Pixel equivalent of STICKY_ACTIONS_WIDTH at default 16px root (for scrollbar math). */
export const STICKY_ACTIONS_WIDTH_PX = 36;

/**
 * Inline width lock for sticky Actions cells / `<col>`.
 * Needed for `table-layout: fixed` and `min-w-full` tables that otherwise dump
 * leftover width into the last column.
 * @param {{ wide?: boolean }} [opts]
 */
export function stickyActionColumnStyle({ wide = false } = {}) {
  const width = wide ? STICKY_ACTIONS_WIDTH_WIDE : STICKY_ACTIONS_WIDTH;
  return {
    width,
    minWidth: width,
    maxWidth: width,
  };
}

/**
 * Keep a mirrored horizontal scrollbar in sync with a table scroller while
 * reserving the sticky Actions column width so the track does not run under
 * the pinned kebab column.
 * @param {HTMLElement | null} tableContainer
 * @param {HTMLElement | null} stickyScrollbar
 * @param {number} [actionsWidthPx]
 */
export function syncStickyScrollbarWidth(
  tableContainer,
  stickyScrollbar,
  actionsWidthPx = STICKY_ACTIONS_WIDTH_PX
) {
  if (!tableContainer || !stickyScrollbar) return;
  const innerDiv = stickyScrollbar.querySelector(".sticky-scrollbar-inner");
  if (!innerDiv) return;
  const width = Math.max(0, tableContainer.scrollWidth - actionsWidthPx);
  innerDiv.style.width = `${width}px`;
  innerDiv.style.minWidth = `${width}px`;
}

/**
 * Whether a CustomTable column should use the pinned Actions treatment.
 * Default: any column with key "actions". Opt in with sticky: "right";
 * opt out with sticky: false.
 * @param {{ key?: string, sticky?: false | "right" }} column
 */
export function isStickyActionsColumn(column) {
  if (!column || column.sticky === false) return false;
  if (column.sticky === "right") return true;
  return column.key === "actions";
}

/**
 * Infer sticky cell fill from row className (selected / archived opacity).
 * @param {string} [rowClassName]
 */
export function resolveStickyTdState(rowClassName = "") {
  const selected = /\bbg-indigo-50\b/.test(rowClassName);
  const archived = /\bopacity-/.test(rowClassName);
  return { selected, archived };
}

/**
 * Resolve sticky Actions header fill so it matches the rest of thead.
 * @param {string} [theadClassName]
 * @returns {'white'|'gray'|'gray-100'}
 */
export function resolveStickyHeaderTone(theadClassName = "") {
  const cls = typeof theadClassName === "string" ? theadClassName : "";
  if (/\bbg-gray-100\b/.test(cls)) return "gray-100";
  if (/\bbg-gray-50\b/.test(cls)) return "gray";
  return "white";
}

/**
 * Sticky Actions <th> classes.
 * Width is locked in CSS (.grid-sticky-actions) so min-w-full tables
 * cannot stretch this column on grids with fewer data columns.
 * @param {'gray'|'gray-100'|'white'} [tone='white'] - match thead background
 * @param {string} [extra]
 */
export function stickyActionThClass(tone = "white", extra = "") {
  const tones = {
    white: { bg: "bg-white", stickyFill: "[--sticky-fill:#ffffff]" },
    gray: { bg: "bg-gray-50", stickyFill: "[--sticky-fill:#f9fafb]" },
    "gray-100": { bg: "bg-gray-100", stickyFill: "[--sticky-fill:#f3f4f6]" },
  };
  const { bg, stickyFill } = tones[tone] || tones.white;
  return [
    GRID_STICKY_ACTIONS,
    // Keep below toolbars/filter popovers (z-40+); only need to sit above scrolling cells.
    "sticky z-[1]",
    bg,
    stickyFill,
    "relative py-3.5 text-center",
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Sticky Actions <td> classes. Syncs background with row hover / selection.
 * @param {{ selected?: boolean, archived?: boolean, extra?: string, align?: 'right'|'center', wide?: boolean }} [opts]
 */
export function stickyActionTdClass({
  selected = false,
  archived = false,
  extra = "",
  align = "center",
  wide = false,
} = {}) {
  let bg =
    "bg-white group-hover:bg-gray-50 [--sticky-fill:#ffffff] group-hover:[--sticky-fill:#f9fafb]";
  if (selected) bg = "bg-indigo-50 [--sticky-fill:#eef2ff]";
  else if (archived) bg = "bg-gray-50 [--sticky-fill:#f9fafb]";

  const textAlign = align === "right" ? "text-right" : "text-center";

  return [
    GRID_STICKY_ACTIONS,
    wide ? "grid-sticky-actions--wide" : "",
    // Keep below toolbars/filter popovers; only need to sit above scrolling cells.
    "sticky z-[1]",
    bg,
    "relative whitespace-nowrap py-4 text-sm font-medium",
    textAlign,
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Clickable data-row classes (add `group` so sticky action cells can sync hover).
 * `interactive: false` keeps the hover for a row the viewer cannot open, without the pointer.
 * @param {{ archived?: boolean, selected?: boolean, interactive?: boolean, extra?: string }} [opts]
 */
export function clickableRowClass({
  archived = false,
  selected = false,
  interactive = true,
  extra = "",
} = {}) {
  const base = interactive ? "group transition-colors cursor-pointer" : "group transition-colors";
  if (archived) {
    return [
      `${base} bg-gray-50`,
      `[&>td:not(.${GRID_STICKY_ACTIONS})]:opacity-40`,
      extra,
    ]
      .filter(Boolean)
      .join(" ");
  }
  if (selected) {
    return [`${base} bg-indigo-50`, extra]
      .filter(Boolean)
      .join(" ");
  }
  return [
    `${base} hover:bg-gray-50`,
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Props for action cells so row-level onClick does not fire. */
export const stickyActionCellProps = {
  onClick: (e) => e.stopPropagation(),
  onMouseDown: (e) => e.stopPropagation(),
};

/** Targets that should not trigger a clickable-row navigation handler. */
export const GRID_ROW_CLICK_IGNORE_SELECTOR =
  'button, a, input, [role="menu"], [data-headlessui-state], .grid-sticky-actions';

/**
 * Whether a row click originated from nested controls / sticky Actions.
 * @param {EventTarget | null} target
 * @param {string} [extraSelector]
 */
export function shouldIgnoreRowClick(target, extraSelector = "") {
  if (!target || typeof target.closest !== "function") return false;
  const selector = extraSelector
    ? `${GRID_ROW_CLICK_IGNORE_SELECTOR}, ${extraSelector}`
    : GRID_ROW_CLICK_IGNORE_SELECTOR;
  return Boolean(target.closest(selector));
}
